import { useDebugValue, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AsyncEntity } from '../../global';
import { RootState } from '../../store';
import { companiesSelectors, companyQuerySelectors } from './companies.state';
import { Company, CompanyDetails, CompanyId, CompanyQuery } from './companies.types';
import { useRootState } from 'src/store/hooks/useRootState';
import qs from 'querystring';
import { AppDispatch } from 'src/index';
import { useUploadFileToBucket } from 'src/hooks/useUploadFileToBucket';
import { uploadCompanyImage } from 'src/features/companies/companies.api';
import { updateCompanyimages } from 'src/features/companies/companies.state';

export const useCompaniesState = (): RootState['companies'] => {
  const companies = useSelector<RootState, RootState['companies']>(state => state.companies);

  useDebugValue(companies);

  return companies;
};

export const useCompaniesValues = (): Company[] => {
  const state = useSelector<RootState, RootState['companies']>(state => state.companies);

  const result = companiesSelectors.selectAll(state);

  useDebugValue(result);

  return result;
};

export const useCompanyById = (id: string): AsyncEntity<Company> | null => {
  const state = useSelector<RootState, RootState['companies']>(state => state.companies);

  const result = companiesSelectors.selectById(state, id) || null;

  useDebugValue(result);

  return result;
};

export const useCompanyDetailsById = (id: string): AsyncEntity<CompanyDetails[]> | undefined => {
  const state = useSelector<RootState, RootState['companies']>(state => state.companies);

  const result = companiesSelectors.selectById(state, id) || null;

  useDebugValue(result);

  return result?.details;
};
export const useCompanyScreensById = (
  id: string
): AsyncEntity<{ type: string; online: boolean; offline: boolean }[]> => {
  const state = useSelector<RootState, RootState['companies']>(state => state.companies);

  const result = companiesSelectors.selectById(state, id) || null;

  useDebugValue(result);

  return result?.deviceScreens || [];
};

export type UseCompaniesByQuery = (query: { name: string }) => CompanyQuery | null;

export const useCompaniesByQuery: UseCompaniesByQuery = ({ name }) => {
  const state = useRootState();
  const companiesState = useSelector<RootState, RootState['companies']>(state => state.companies);
  const queryState = companyQuerySelectors.selectById(state, qs.stringify({ name }));
  let result: ReturnType<UseCompaniesByQuery> = null;

  if (queryState) {
    const nextItems = queryState.items.reduce((acc, id) => {
      const company = companiesSelectors.selectById(companiesState, id as CompanyId);

      if (company) {
        acc.push(company);
      }

      return acc;
    }, [] as AsyncEntity<Company>[]);

    result = { ...queryState, items: nextItems };
  }

  useDebugValue(result);

  return result;
};

export type UseCardPoster = (params: { companyId: string }) => {
  progress: number;
  uploadContentImage: (file: File, imageType: 'ver' | 'hor') => void;
};

export const useCompanyImage: UseCardPoster = ({ companyId }) => {
  const dispatch: AppDispatch = useDispatch();
  const [progress, uploadFile] = useUploadFileToBucket();

  const uploadContentImage = useCallback(
    async (file: File, imageType: 'ver' | 'hor') => {
      const { name: filename } = file;

      const credentials = await uploadCompanyImage({
        companyId,
        data: { filename },
      });

      const { url, fields } = credentials.data;
      const { key } = fields;

      if (credentials.status < 400) {
        const response = await uploadFile({ ...credentials.data, file });
        const data = imageType === 'ver' ? { imageVer: `${url}/${key}` } : { image: `${url}/${key}` };
        if (response) {
          dispatch(
            updateCompanyimages({
              companyId,
              data: data,
            })
          );
        }
      }
    },
    [companyId, dispatch, uploadFile]
  );

  return { progress, uploadContentImage };
};
